import {
  readOneSyncingMissing,
  readWhenDownloaded,
  type EntityReadStorage,
} from 'serviceWorker/channelServers/entitySource';
import * as dbIssues from 'serviceWorker/db/issues/issues';
import * as dbIssuesService from 'serviceWorker/db/issues/issuesService';
import type { IssueEntity } from 'serviceWorker/repository/issue/entity';
import type { IssueModel } from 'shared/domain/issue/issueModel';
import type { FilteredIssues, IssueListAddress, IssuesQuery } from 'shared/domain/issue/issueQuery';
import { issueContract } from 'shared/domain/issue/issueContract';
import { createThrottledReadStorage } from 'serviceWorker/services/factories/createThrottledReadStorage';
import { fetchIssueById } from './fetchIssueById';
import { issuesSync } from './index';
import {
  getFilteredIssueAddresses,
  getFilteredIssueModels,
  getIssueModel,
  getIssueModelsOfInspection,
  requireStoredIssue,
} from './issueReads';

const SYNC_AFTER_READ_INTERVAL_MS = 5000;

/**
 * Reads refresh the issues unless a refresh runs or ended less than 5 s ago:
 * the grid reads on every page and filter change.
 */
export const issueStorage: EntityReadStorage = {
  ...createThrottledReadStorage({
    service: dbIssuesService,
    contract: issueContract,
    collectionEvent: issueContract.collectionEvent,
    synced: issuesSync,
    refreshIntervalMs: SYNC_AFTER_READ_INTERVAL_MS,
  }),
  fetchOne: fetchIssueById,
};

/**
 * Issues of the selected project stored in IndexedDB. The page never reads all
 * of them: every read is a query answered once the first download ended.
 */
export const issueSource = {
  /**
   * A missing issue (created since the last sync, deleted, opened from a link
   * before the download ended) is synced, then fetched alone by its id.
   */
  async getOneById(id: string): Promise<IssueModel> {
    return requireStoredIssue(id, await readOneSyncingMissing(issueStorage, id, () => getIssueModel(id)));
  },

  getFiltered(query: IssuesQuery): Promise<FilteredIssues> {
    return readWhenDownloaded(issueStorage, () => getFilteredIssueModels(query));
  },

  getFilteredIdList(query: IssuesQuery): Promise<IssueListAddress[]> {
    return readWhenDownloaded(issueStorage, () => getFilteredIssueAddresses(query));
  },

  /** An indexed read of one inspection's issues: no project-wide refresh, as before. */
  getOfInspection(inspectionId: string, timezone: string): Promise<IssueModel[]> {
    return readWhenDownloaded({ ...issueStorage, syncAfterRead: false }, () =>
      getIssueModelsOfInspection(inspectionId, timezone)
    );
  },

  /** Active issues as stored, for the worker's own calculations (financial data). */
  getActiveEntities(): Promise<IssueEntity[]> {
    return readWhenDownloaded({ ...issueStorage, syncAfterRead: false }, () => dbIssues.get());
  },

  sync: (): void => issuesSync.sync(),
};
